import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  X,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader,
  BookOpen
} from 'lucide-react';
import { theme } from '../styles/theme';
import {
  KnowledgeBaseApi,
  CompanyDocumentCategory,
  CompanyDocumentCreateRequest,
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_GROUPS,
} from '../services/knowledgeBaseApi';
import { useToastNotifications } from '../context/ToastContext';
import { uploadService } from '../services/uploadService';

// Presentation order for the Category dropdown's <optgroup> blocks.
// The CATEGORY_GROUPS dict's natural insertion order put the newer
// groups (Performance / Risk / Social) at the top, which buries the
// most common starter categories (Company Profile, Team) below the
// fold. This order matches the wizard's flow: identity first, then
// people, then evidence, then proof, then policies, then specialised
// categories, then escape-hatch.
const CATEGORY_GROUP_ORDER: ReadonlyArray<keyof typeof CATEGORY_GROUPS> = [
  'Company Profile',
  'Team & Personnel',
  'Experience & Case Studies',
  'Technical Capabilities',
  'Capabilities & Services',
  'Certifications & Accreditations',
  'Financial Documents',
  'Policies & Procedures',
  'Performance & Track Record',
  'Risk & Innovation',
  'Social Value & Capacity',
  'Other',
];

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCategory?: CompanyDocumentCategory;
  onSuccess?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing[6]};
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${theme.shadows.xl};
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  border-radius: ${theme.borderRadius.lg};
  color: white;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.error[100]};
    border-color: ${theme.colors.error[500]};
    color: ${theme.colors.error[600]};
  }
`;

const ModalContent = styled.div`
  padding: ${theme.spacing[6]};
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const InputLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const TextInput = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  font-family: inherit;
  background: ${theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: ${theme.shadows.focus};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  font-family: inherit;
  min-height: 120px;
  resize: vertical;
  background: ${theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: ${theme.shadows.focus};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const Select = styled.select<{ $disabled?: boolean }>`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  transition: all ${theme.transitions.base};
  font-family: inherit;
  background: ${props => props.$disabled ? theme.colors.backgroundSecondary : theme.colors.background};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.7 : 1};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: ${theme.shadows.focus};
  }
`;

const DropZone = styled.div<{ $isDragOver?: boolean; $hasFile?: boolean }>`
  border: 2px dashed ${({ $isDragOver, $hasFile }) =>
    $hasFile ? theme.colors.success[500] :
    $isDragOver ? theme.colors.primary[500] : theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  text-align: center;
  background: ${({ $isDragOver, $hasFile }) =>
    $hasFile ? theme.colors.success[100] :
    $isDragOver ? theme.colors.primary[50] : theme.colors.backgroundSecondary};
  transition: all ${theme.transitions.base};
  cursor: pointer;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${theme.colors.primary[50]};
  }
`;

const DropZoneIcon = styled.div<{ $hasFile?: boolean }>`
  width: 56px;
  height: 56px;
  margin: 0 auto ${theme.spacing[3]} auto;
  background: ${({ $hasFile }) => $hasFile ? theme.colors.success[100] : theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $hasFile }) => $hasFile ? theme.colors.success[600] : theme.colors.primary[600]};
`;

const DropZoneTitle = styled.h3`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const DropZoneDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin: 0;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.lg};
  margin-top: ${theme.spacing[3]};
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[600]};
  flex-shrink: 0;
`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const ModalFooter = styled.div`
  padding: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
  flex-shrink: 0;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  border: none;

  ${({ $variant }) => $variant === 'primary' ? `
    background: ${theme.colors.primary[500]};
    color: white;

    &:hover:not(:disabled) {
      background: ${theme.colors.primary[600]};
    }
  ` : `
    background: ${theme.colors.backgroundSecondary};
    color: ${theme.colors.textPrimary};
    border: 1px solid ${theme.colors.border};

    &:hover:not(:disabled) {
      background: ${theme.colors.backgroundTertiary};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.error[700]};
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: ${theme.spacing[4]};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  initialCategory,
  onSuccess
}) => {
  const { showSuccess, showError } = useToastNotifications();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');
  // UX: hide secondary fields (Summary/Tags/category-specific) by default —
  // most uploads are just file + auto-filled title. Advanced fields appear
  // on demand.
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    category: initialCategory || 'company_overview' as CompanyDocumentCategory,
    content: '',
    summary: '',
    tags: '',
    // Enhanced metadata fields
    success_metrics: [] as string[],
    question_type_mapping: [] as string[],
    tender_relevance_tags: [] as string[],
    risk_type: '',
    risk_impact: '',
    innovation_type: '',
    social_value_type: ''
  });

  // Update category when initialCategory changes (when modal opens)
  React.useEffect(() => {
    if (initialCategory && isOpen) {
      setFormData(prev => ({ ...prev, category: initialCategory }));
    }
  }, [initialCategory, isOpen]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        category: 'company_overview',
        content: '',
        summary: '',
        tags: '',
        success_metrics: [],
        question_type_mapping: [],
        tender_relevance_tags: [],
        risk_type: '',
        risk_impact: '',
        innovation_type: '',
        social_value_type: ''
      });
      setSelectedFile(null);
      setShowAdvanced(false);
      setError('');
    }
  }, [isOpen]);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type (PDF or common document formats)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, Word document, or text file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setError('');

    // Always auto-fill title from filename when a new file is picked.
    // Previous behaviour only filled if title was empty — but if the user
    // swaps the file (e.g. picks the wrong PDF first), they expect the
    // title to update too. Backend will use this title verbatim;
    // user can still edit it before upload.
    const fileNameWithoutExt = file.name
      .replace(/\.[^/.]+$/, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    // Title-case first letters of each word for nicer display
    const prettified = fileNameWithoutExt
      .split(' ')
      .map(w => (w.length > 3 && w === w.toLowerCase()) ? w[0].toUpperCase() + w.slice(1) : w)
      .join(' ');
    setFormData(prev => ({ ...prev, title: prettified }));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Please provide a document title');
      return;
    }

    if (!formData.content.trim() && !selectedFile) {
      setError('Please upload a file or provide document content');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      // ── Path A: file selected → use the new multipart upload endpoint
      // that extracts text via PyMuPDF / python-docx, auto-classifies into
      // a CompanyDocumentCategory, and persists everything in one shot.
      if (selectedFile) {
        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL ||
          (process.env.NODE_ENV === 'production'
            ? 'https://api-beta-open-tender.nat-quest.com'
            : 'http://localhost:2024');
        const tokenRaw = localStorage.getItem('opentender_tokens');
        const headers: Record<string, string> = {};
        if (tokenRaw) {
          try {
            const tok = JSON.parse(tokenRaw);
            if (tok?.accessToken) headers['Authorization'] = `Bearer ${tok.accessToken}`;
          } catch {}
        }
        const fd = new FormData();
        fd.append('file', selectedFile);
        if (formData.title?.trim()) fd.append('title', formData.title.trim());
        if (formData.category) fd.append('category', String(formData.category));
        // Always auto-classify when the user uploads a raw file. We'll honor
        // an explicit category if they've chosen one (the backend prefers
        // the user value over the LLM suggestion).
        fd.append('auto_classify', 'true');
        const resp = await fetch(`${apiBaseUrl}/api/knowledge-base/documents/upload-file`, {
          method: 'POST',
          headers,
          body: fd,
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Upload failed (${resp.status}): ${txt.slice(0, 200)}`);
        }
        showSuccess('Document uploaded — text extracted and auto-classified.');
        onSuccess?.();
        onClose();
        return;
      }

      // ── Path B: no file, content pasted directly. Use the existing JSON
      // endpoint as before.
      const documentData: CompanyDocumentCreateRequest = {
        title: formData.title.trim(),
        category: formData.category,
        content: formData.content.trim(),
        summary: formData.summary.trim() || undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        // Enhanced metadata fields
        success_metrics: formData.success_metrics.length > 0 ? formData.success_metrics : undefined,
        question_type_mapping: formData.question_type_mapping.length > 0 ? formData.question_type_mapping : undefined,
        tender_relevance_tags: formData.tender_relevance_tags.length > 0 ? formData.tender_relevance_tags : undefined,
        risk_type: formData.risk_type || undefined,
        risk_impact: formData.risk_impact || undefined,
        innovation_type: formData.innovation_type || undefined,
        social_value_type: formData.social_value_type || undefined
      };

      const response = await KnowledgeBaseApi.createDocument(documentData);

      if (response.success) {
        showSuccess('Document uploaded successfully');
        onSuccess?.();
        onClose();
      } else {
        showError('Upload failed', response.error || 'Failed to upload document');
        setError(response.error || 'Failed to upload document');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      showError('Upload failed', errorMessage);
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <TitleIcon>
              <BookOpen size={20} />
            </TitleIcon>
            Upload Document
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && (
            <ErrorMessage>
              <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>{error}</span>
            </ErrorMessage>
          )}

          <FormSection>
            {/* PRIMARY ACTION — drop the file. Title + category derive
                from this. Title is auto-filled on file pick; category is
                pre-set by the calling KB tile. The user mostly just drops
                a file and clicks Upload. */}
            <InputGroup>
              <DropZone
                $isDragOver={isDragOver}
                $hasFile={!!selectedFile}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleDropZoneClick}
              >
                <DropZoneIcon $hasFile={!!selectedFile}>
                  {selectedFile ? <CheckCircle size={24} /> : <Upload size={24} />}
                </DropZoneIcon>
                <DropZoneTitle>
                  {selectedFile ? 'File Selected' : 'Drop file here or click to browse'}
                </DropZoneTitle>
                <DropZoneDescription>
                  PDF, Word, or Text files (Max 10MB)
                </DropZoneDescription>
              </DropZone>

              {selectedFile && (
                <FileInfo>
                  <FileIcon>
                    <FileText size={20} />
                  </FileIcon>
                  <FileDetails>
                    <FileName>{selectedFile.name}</FileName>
                    <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
                  </FileDetails>
                </FileInfo>
              )}

              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileInputChange}
              />
            </InputGroup>

            {/* Title — auto-filled from filename; user can tweak.
                For paste-only flow (no file), this is required up-front. */}
            <InputGroup>
              <InputLabel>
                Document Title *
                {selectedFile && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#6b7280',
                    marginLeft: 8,
                  }}>
                    (auto-filled from filename — edit if needed)
                  </span>
                )}
              </InputLabel>
              <TextInput
                type="text"
                placeholder="e.g., Company Overview 2024"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                disabled={isUploading}
              />
            </InputGroup>

            {/* Category — usually pre-set by the KB tile that opened this
                modal. We keep it visible but disabled in that case so the
                user can verify what bucket the doc is going into. */}
            <InputGroup>
              <InputLabel>
                Category *
                {initialCategory && (
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#6b7280',
                    marginLeft: 8,
                  }}>
                    (set by category tile)
                  </span>
                )}
              </InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as CompanyDocumentCategory }))}
                disabled={isUploading || !!initialCategory}
                $disabled={!!initialCategory}
              >
                {CATEGORY_GROUP_ORDER.map(groupName => {
                  const cats = CATEGORY_GROUPS[groupName] || [];
                  if (cats.length === 0) return null;
                  return (
                    <optgroup key={groupName} label={groupName}>
                      {cats.map(value => (
                        <option key={value} value={value}>
                          {CATEGORY_DISPLAY_NAMES[value as CompanyDocumentCategory] || value}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
            </InputGroup>

            {/* Document Content — only relevant when no file is being
                uploaded. With a file, the content IS the file body
                (extracted backend-side); asking the user to paste it
                separately is redundant. */}
            {!selectedFile && (
              <InputGroup>
                <InputLabel>
                  Document Content
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 400,
                    color: '#6b7280',
                    marginLeft: 8,
                  }}>
                    (only needed if you're not uploading a file)
                  </span>
                </InputLabel>
                <TextArea
                  placeholder="Paste document text here, or upload a file above instead"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  disabled={isUploading}
                />
              </InputGroup>
            )}

            {/* Advanced fields — Summary, Tags, and category-specific
                metadata. Default hidden; revealed by toggle. Most uploads
                don't need these — the system auto-summarises and the
                category itself is the strongest tag. */}
            <button
              type="button"
              onClick={() => setShowAdvanced(prev => !prev)}
              disabled={isUploading}
              style={{
                background: 'none',
                border: 'none',
                color: '#4f46e5',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                padding: '6px 0',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 4,
              }}
            >
              {showAdvanced ? '▾' : '▸'} {showAdvanced ? 'Hide' : 'Show'} advanced fields (summary, tags, metadata)
            </button>

            {showAdvanced && (
              <>
                <InputGroup>
                  <InputLabel>Summary (Optional)</InputLabel>
                  <TextInput
                    type="text"
                    placeholder="Brief summary of the document"
                    value={formData.summary}
                    onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                    disabled={isUploading}
                  />
                </InputGroup>

                <InputGroup>
                  <InputLabel>Tags (Optional)</InputLabel>
                  <TextInput
                    type="text"
                    placeholder="e.g., finance, 2024, annual (comma-separated)"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    disabled={isUploading}
                  />
                </InputGroup>

                {/* Category-specific metadata — only renders when the
                    chosen category has tunable structured fields. */}
                {formData.category === 'past_performance' && (
                  <InputGroup>
                    <InputLabel>Success Metrics (Optional)</InputLabel>
                    <TextInput
                      type="text"
                      placeholder="e.g., 98% on-time delivery, Budget adherence 99.5% (comma-separated)"
                      value={formData.success_metrics.join(', ')}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        success_metrics: e.target.value ? e.target.value.split(',').map(s => s.trim()) : []
                      }))}
                      disabled={isUploading}
                    />
                  </InputGroup>
                )}

                {formData.category === 'risk_register' && (
                  <>
                    <InputGroup>
                      <InputLabel>Risk Type (Optional)</InputLabel>
                      <Select
                        value={formData.risk_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, risk_type: e.target.value }))}
                        disabled={isUploading}
                      >
                        <option value="">Select risk type</option>
                        <option value="operational">Operational</option>
                        <option value="financial">Financial</option>
                        <option value="technical">Technical</option>
                        <option value="reputational">Reputational</option>
                      </Select>
                    </InputGroup>
                    <InputGroup>
                      <InputLabel>Risk Impact (Optional)</InputLabel>
                      <Select
                        value={formData.risk_impact}
                        onChange={(e) => setFormData(prev => ({ ...prev, risk_impact: e.target.value }))}
                        disabled={isUploading}
                      >
                        <option value="">Select impact level</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </Select>
                    </InputGroup>
                  </>
                )}

                {formData.category === 'innovation_case' && (
                  <InputGroup>
                    <InputLabel>Innovation Type (Optional)</InputLabel>
                    <Select
                      value={formData.innovation_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, innovation_type: e.target.value }))}
                      disabled={isUploading}
                    >
                      <option value="">Select innovation type</option>
                      <option value="process">Process</option>
                      <option value="technology">Technology</option>
                      <option value="service">Service</option>
                      <option value="methodology">Methodology</option>
                    </Select>
                  </InputGroup>
                )}

                {formData.category === 'social_value_evidence' && (
                  <InputGroup>
                    <InputLabel>Social Value Type (Optional)</InputLabel>
                    <Select
                      value={formData.social_value_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, social_value_type: e.target.value }))}
                      disabled={isUploading}
                    >
                      <option value="">Select social value type</option>
                      <option value="employment">Employment</option>
                      <option value="environment">Environment</option>
                      <option value="community">Community</option>
                      <option value="wellbeing">Wellbeing</option>
                    </Select>
                  </InputGroup>
                )}

                {/* Cross-category retrieval hints — power-user knobs that
                    nudge the retriever toward this doc on certain queries. */}
                <InputGroup>
                  <InputLabel>Question Types This Answers (Optional)</InputLabel>
                  <TextInput
                    type="text"
                    placeholder="e.g., past_performance, capability, risk_management (comma-separated)"
                    value={formData.question_type_mapping.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      question_type_mapping: e.target.value ? e.target.value.split(',').map(s => s.trim()) : []
                    }))}
                    disabled={isUploading}
                  />
                </InputGroup>

                <InputGroup>
                  <InputLabel>Tender Relevance Tags (Optional)</InputLabel>
                  <TextInput
                    type="text"
                    placeholder="e.g., healthcare, IT services, public sector (comma-separated)"
                    value={formData.tender_relevance_tags.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tender_relevance_tags: e.target.value ? e.target.value.split(',').map(s => s.trim()) : []
                    }))}
                    disabled={isUploading}
                  />
                </InputGroup>
              </>
            )}
          </FormSection>
        </ModalContent>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button $variant="primary" onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload Document
              </>
            )}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default DocumentUploadModal;
